import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircle, FileCode2, CheckSquare, Layers,
  ArrowRight, AlertCircle, RefreshCw
} from 'lucide-react';
import { messageApi } from '../../services/iso20022Api';

export default function Iso20022Dashboard() {
  const [schemas, setSchemas] = useState([]);
  const [health,  setHealth]  = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const schemaRes = await messageApi.listSchemas();
      setSchemas(schemaRes.schemas ?? schemaRes ?? []);
      setHealth('ok');
    } catch (e) {
      setError(e.message);
      setHealth('error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const groups = schemas.reduce((acc, s) => {
    const prefix = s.messageType?.split('.')[0] ?? 'other';
    if (!acc[prefix]) acc[prefix] = [];
    acc[prefix].push(s);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">ISO 20022 Corridor</h1>
        <p className="text-sm text-gray-500 mt-1">XMLDSig signing · Schema validation · Message generation</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-5 flex items-center space-x-4">
          {loading ? (
            <RefreshCw className="w-8 h-8 text-gray-400 animate-spin" />
          ) : health === 'ok' ? (
            <CheckCircle className="w-8 h-8 text-green-500" />
          ) : (
            <AlertCircle className="w-8 h-8 text-red-500" />
          )}
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide">Engine Status</p>
            <p className="text-lg font-semibold text-gray-900">
              {loading ? 'Checking…' : health === 'ok' ? 'Online' : 'Unreachable'}
            </p>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5 flex items-center space-x-4">
          <Layers className="w-8 h-8 text-amber-500" />
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide">Message Schemas</p>
            <p className="text-lg font-semibold text-gray-900">{loading ? '…' : schemas.length}</p>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5 flex items-center space-x-4">
          <FileCode2 className="w-8 h-8 text-blue-500" />
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide">Schema Families</p>
            <p className="text-lg font-semibold text-gray-900">{loading ? '…' : Object.keys(groups).length}</p>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
          <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-medium text-red-800">Could not reach ISO 20022 engine</p>
            <p className="text-xs text-red-600 mt-1">{error}</p>
          </div>
          <button onClick={load} className="ml-auto text-xs text-red-600 underline">Retry</button>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Link to="/iso20022/generate"
          className="bg-white rounded-xl border border-gray-200 p-5 hover:border-amber-400 hover:shadow-sm transition group">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <FileCode2 className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900">Message Generator</p>
                <p className="text-xs text-gray-500">Generate signed ISO 20022 messages</p>
              </div>
            </div>
            <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-amber-600 transition" />
          </div>
        </Link>

        <Link to="/iso20022/validate"
          className="bg-white rounded-xl border border-gray-200 p-5 hover:border-blue-400 hover:shadow-sm transition group">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <CheckSquare className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900">Message Validator</p>
                <p className="text-xs text-gray-500">Validate &amp; verify XML signatures</p>
              </div>
            </div>
            <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-blue-600 transition" />
          </div>
        </Link>
      </div>

      {!loading && schemas.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h2 className="text-sm font-semibold text-gray-900 mb-4">Available Message Schemas</h2>
          <div className="space-y-4">
            {Object.entries(groups).sort().map(([prefix, items]) => (
              <div key={prefix}>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
                  {prefix.toUpperCase()} ({items.length})
                </p>
                <div className="flex flex-wrap gap-2">
                  {items.map((s) => (
                    <Link key={s.messageType} to="/iso20022/generate"
                      state={{ preselect: s.messageType }}
                      className="inline-flex items-center px-2.5 py-1 rounded-md bg-gray-50 border border-gray-200 text-xs font-mono text-gray-700 hover:bg-amber-50 hover:border-amber-300 transition">
                      {s.messageType}
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
